/**
 * Handle /r/call/:callId
 */

import {
  baseContext,
  browse3ContextGen,
  PEEK_PARAM,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/context';
import {useWFHooks} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {isWeaveObjectRef, parseRefMaybe, refUri} from '@wandb/weave/react';
import React from 'react';
import {Redirect, useParams} from 'react-router-dom';

import {WEAVE_SLUG} from '../../routes/paths';

const projectRoot = (entityName: string, projectName: string) => {
  return `/${entityName}/${projectName}/${WEAVE_SLUG}`;
};

type RedirectCallParams = {
  entityName: string;
  projectName: string;
  callId: string;
};

export const RedirectCall = () => {
  const baseRouter = browse3ContextGen(projectRoot);

  // First we get the call details from the URL
  const {
    entityName: entity,
    projectName: project,
    callId,
  } = useParams<RedirectCallParams>();

  // Next, we fetch the call from the DB
  // TODO: If callId is not a valid UUID we could skip querying. Could use validate from uuid.
  const {useCall} = useWFHooks();
  const {result: call, loading} = useCall({
    key: {
      entity,
      project,
      callId,
    },
  });

  // Just return null if we are still loading
  if (loading) {
    return null;
  }

  // If we don't have a call, we redirect to the project root
  if (!call) {
    // Rather than printing an error, we redirect
    // to the weave root for the project
    return <Redirect to={baseRouter.projectUrl(entity, project)} />;
  }

  // If we did find the call, we want to use it as the peek component
  const callPeekComponent = baseContext.callUIUrl(
    entity,
    project,
    call.traceId,
    callId,
    undefined
  );

  // traceCall should be present, but we check just in case
  // the path where it is null is old and deprecated
  const op_name = call.traceCall?.op_name;

  // Next, we parse the op_name (which could be a plain string or a ref)
  const parsedOpName = op_name ? parseRefMaybe(op_name) : null;
  let opVersionRefs: string[] = [];

  // We need to check if the op_name is actually a ref, and if so,
  // make it a wildcard ref so that we can see all versions of the op.
  if (parsedOpName && isWeaveObjectRef(parsedOpName)) {
    parsedOpName.artifactVersion = '*';
    opVersionRefs = [refUri(parsedOpName)];
  }

  // Finally, we build the base path to the call table (including any opVersionRefs)
  const path = baseRouter.callsUIUrl(entity, project, {
    opVersionRefs,
  });

  // Finally we tack on the peek param to the URL
  const searchParams = new URLSearchParams();
  searchParams.set(PEEK_PARAM, callPeekComponent);
  const newSearch = searchParams.toString();
  const delim = path.includes('?') ? '&' : '?';
  const targetUrl = `${path}${delim}${newSearch}`;

  // And redirect to the call table with the peek param
  return <Redirect to={targetUrl} />;
};
